// ================================
// hooks/useNotificationCounter.js
// ================================
// Le compteur de la cloche de l'administration.
//
// Une SEULE instance, dans AdministrationLayout : la pastille (NotificationBadge)
// et le tiroir (NotificationModal) reçoivent ses valeurs en props. Chaque
// instance interrogeait /api/notifications/count pour son compte, et chaque
// appel coûte une validation de session et des lectures d'INFORMATION_SCHEMA
// côté serveur : deux instances, c'étaient deux boucles.
//
// Le serveur renvoie aussi `dernierId`, le plus grand id des notifications non
// lues et non archivées : un repère qui ne fait que monter quand une
// notification arrive, là où le nombre bouge aussi quand on désarchive ou qu'on
// lit. C'est lui qui fait sonner la cloche (dans la coquille). Il vaut null
// quand il n'y a aucune non lue.
//
// `dernierIdConnu` passe à true dès qu'une réponse porte le champ, même à
// null, et y reste : la coquille sait alors qu'elle a reçu sa première lecture
// (qui sert de référence, sans son) et distingue « aucune non lue » d'un
// serveur plus ancien qui ne renvoie pas le champ (rien ne sonne alors).
//
// Le sondage s'arrête quand l'onglet est caché et reprend, par un appel
// immédiat, quand on y revient.

import { useState, useEffect, useCallback, useRef } from 'react';

// Cadence du sondage, inchangée : premier tour une seconde après le montage,
// puis toutes les deux secondes.
const CADENCE_MS = 2000;
const DELAI_PREMIER_TOUR_MS = 1000;

const API_BASE_URL = '/api';

const getTenantFromHost = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'www';
  const parts = hostname.split('.');
  if (parts.length >= 2 && parts[parts.length - 1] === 'localhost') return parts[0];
  return parts.length >= 3 ? parts[0] : null;
};

const getNotificationHeaders = (token) => {
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
  const tenant = getTenantFromHost();
  const anneeScolaireId = localStorage.getItem('annee_scolaire_id');
  if (tenant) headers['X-Tenant'] = tenant;
  if (anneeScolaireId) headers['X-Annee-Scolaire-Id'] = anneeScolaireId;
  return headers;
};

// Un id positif, ou null (aucune non lue, ou champ absent d'un serveur plus
// ancien : `dernierIdConnu` fait la différence).
const lireDernierId = (valeur) => {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  const id = Number(valeur);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const ongletCache = () => typeof document !== 'undefined' && document.hidden === true;

const useNotificationCounter = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [dernierId, setDernierId] = useState(null);
  const [dernierIdConnu, setDernierIdConnu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const intervalRef = useRef(null);
  const isComponentMountedRef = useRef(true);
  // Un tour de sondage ne part pas tant que le précédent n'a pas répondu ; un
  // rafraîchissement demandé par le tiroir (après lecture, archivage) part
  // quand même, et seule la réponse de l'appel le plus récent est retenue.
  const enVolRef = useRef(false);
  const sequenceRef = useRef(0);

  /**
   * Récupérer le compteur de notifications non lues (et le repère dernierId)
   */
  const fetchUnreadCount = useCallback(async ({ afficherChargement = false, forcer = false } = {}) => {
    if (!isComponentMountedRef.current) return;
    if (enVolRef.current && !forcer) return;

    const token = localStorage.getItem('token');
    if (!token) {
      setUnreadCount(0);
      setDernierId(null);
      return;
    }

    const numero = ++sequenceRef.current;
    const estLeDernierAppel = () => numero === sequenceRef.current;
    enVolRef.current = true;
    if (afficherChargement) setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/notifications/count`, {
        headers: getNotificationHeaders(token)
      });

      if (!response.ok) {
        if (response.status === 401) {
          if (isComponentMountedRef.current && estLeDernierAppel()) setUnreadCount(0);
          return;
        }
        throw new Error(`Erreur ${response.status}`);
      }

      const data = await response.json();
      if (!isComponentMountedRef.current || !estLeDernierAppel()) return;

      if (data.success && data.data) {
        setUnreadCount(data.data.unreadCount || 0);
        // Le champ présent, même à null, vaut réponse : la coquille peut poser
        // sa référence (y compris « aucune non lue »).
        if (Object.prototype.hasOwnProperty.call(data.data, 'dernierId')) setDernierIdConnu(true);
        setDernierId(lireDernierId(data.data.dernierId));
      } else {
        setUnreadCount(0);
      }
    } catch (erreur) {
      console.error('[NotificationCounter] Erreur:', erreur);
      if (isComponentMountedRef.current && estLeDernierAppel()) {
        setError(erreur.message);
        setUnreadCount(0);
      }
    } finally {
      if (estLeDernierAppel()) {
        enVolRef.current = false;
        if (isComponentMountedRef.current && afficherChargement) setLoading(false);
      }
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback((interval = CADENCE_MS) => {
    stopPolling();
    intervalRef.current = setInterval(() => {
      // Filet : un onglet caché ne sonde pas, même si l'événement de
      // visibilité s'est perdu.
      if (ongletCache()) return;
      fetchUnreadCount();
    }, interval);
  }, [fetchUnreadCount, stopPolling]);

  /** Relire le compteur tout de suite (après lecture, « tout lire », archivage). */
  const rafraichir = useCallback(() => fetchUnreadCount({ forcer: true }), [fetchUnreadCount]);

  useEffect(() => {
    isComponentMountedRef.current = true;
    let startTimer = null;

    // Onglet visible au montage : premier appel, puis le sondage. Caché (ouvert
    // en arrière-plan) : rien avant qu'on y vienne.
    if (!ongletCache()) {
      fetchUnreadCount({ afficherChargement: true });
      startTimer = setTimeout(() => {
        if (!ongletCache()) startPolling(CADENCE_MS);
      }, DELAI_PREMIER_TOUR_MS);
    }

    const surChangementDeVisibilite = () => {
      if (ongletCache()) {
        stopPolling();
        return;
      }
      fetchUnreadCount();
      startPolling(CADENCE_MS);
    };
    document.addEventListener('visibilitychange', surChangementDeVisibilite);

    return () => {
      isComponentMountedRef.current = false;
      if (startTimer) clearTimeout(startTimer);
      document.removeEventListener('visibilitychange', surChangementDeVisibilite);
      stopPolling();
    };
  }, [fetchUnreadCount, startPolling, stopPolling]);

  return {
    unreadCount,
    dernierId,
    dernierIdConnu,
    loading,
    error,
    rafraichir,
    // Noms historiques, gardés pour ne rien casser.
    refresh: rafraichir,
    updateCount: rafraichir,
    startPolling,
    stopPolling
  };
};

export default useNotificationCounter;
